import { usePackageStore } from './package'
import { useCmsStore } from './cms'
import bookingService from '~/services/bookingService'
import packageService from '~/services/packageService'
import couponService from '~/services/couponService'
import preorderService from '~/services/preorderService'
import Booking from '~/config/Booking'

export const useBookStore = defineStore('BookStore', {
  state: () => {
    return {
      packageId: null,
      /* getters.createHotelBook[] */
      hotels: [],
      baseHotels: [],
      startDate: null,
      endDate: null,
      tickets: [],
      isLimitedTicketNumber: false,
      numberOfTickets:
        Booking.defaultNumberOfAdults + Booking.defaultNumberOfChildren,
      transit: null,
      outwardFlight: null,
      returnFlight: null,
      adults: Booking.defaultNumberOfAdults,
      children: Booking.defaultNumberOfChildren,
      rooms: Booking.defaultRoom,
      contactInfo: null,
      passengers: [],
      numberOfHasTicketPassengers: 0,
      changedPrices: [],
      comment: '',
      hasInsurance: '',
      hasMatchInsurance: '',
      hasAcceptedDifferentHomeAirports: false,
      hasAcceptedExtraordinaryConditions: false,
      hasAcceptedGeneralTerms: false,
      hasAcceptedPremiumUpgrade: false,
      hasAcceptedMatchRisk: false,
      isResidualSaleBooking: false,
      coupon: '',
      isChangedPricesAccepted: false,
      isTransitLessBooking: false,
      isFlightLessBooking: false,
      isHotelLessBooking: false,
      tripExtendedDays: 0
    }
  },
  getters: {
    getExtraFees: state => () => {
      const packageStore = usePackageStore()
      let extraFees = 0

      if (state.hasMatchInsurance) {
        extraFees += packageStore.package?.extraFees?.matchInsuranceFee || 0
      }

      // Add premium upgrade per person
      if (state.hasAcceptedPremiumUpgrade) {
        extraFees += packageStore.package?.premiumPrice || 0
        extraFees += state.extraServicesUpgradeFee()
      }

      const numberOfPassengers = packageStore.numberOfPassengers || 0

      return extraFees * numberOfPassengers
    },

    calculateServicesPrice: state => () => {
      const packageStore = usePackageStore()
      const numberOfPassengers = packageStore.numberOfPassengers || 0

      const transitPrice = state.isTransitLessBooking
        ? 0
        : (parseFloat(state.transit?.price || 0) * numberOfPassengers)
      const outwardFlightPrice = state.isFlightLessBooking
        ? 0
        : parseFloat(state.outwardFlight?.price || 0)
      const returnFlightPrice = state.isFlightLessBooking
        ? 0
        : parseFloat(state.returnFlight?.price || 0)
      const hotelsPrice = state.getHotelTotalPrice()
      const ticketsPrice = state.getTicketPrice()
      const basePrice = parseFloat(packageStore.basePrice || 0)
      const extraFess = state.getExtraFees()

      return {
        transitPrice,
        outwardFlightPrice,
        returnFlightPrice,
        hotelsPrice,
        ticketsPrice,
        basePrice,
        extraFess
      }
    },

    calculateTotalPrice: state => () => {
      const {
        transitPrice,
        outwardFlightPrice,
        returnFlightPrice,
        hotelsPrice,
        ticketsPrice,
        basePrice,
        extraFess
      } = state.calculateServicesPrice()

      const packageStore = usePackageStore()
      const numberOfPassengers = packageStore.numberOfPassengers || 0

      const flightlessBookingDeduction =
        state.isFlightLessBooking && !state.isResidualSaleBooking
          ? state.getBookingDiscount().flight
          : 0
      const hotellessBookingDeduction =
        state.isHotelLessBooking && !state.isResidualSaleBooking
          ? state.getBookingDiscount().hotel
          : 0

      const total =
        numberOfPassengers * basePrice +
        transitPrice +
        outwardFlightPrice +
        returnFlightPrice +
        hotelsPrice +
        ticketsPrice +
        extraFess -
        numberOfPassengers * flightlessBookingDeduction -
        numberOfPassengers * hotellessBookingDeduction
      const couponDiscount = state.calculateCouponDiscount(total)

      return Math.ceil(total - couponDiscount)
    },

    getHotelTotalPrice: state => () => {
      if (state.isHotelLessBooking) {
        return 0
      }

      return state.hotels.reduce(
        (carry, hotel) =>
          carry +
          hotel.rooms.reduce((prev, cur) => prev + parseFloat(cur.price), 0),
        0
      )
    },

    getTicketPrice: state => () => {
      const ticketTotalPrice = state.tickets.reduce(
        (prev, cur) => prev + parseFloat(cur.price),
        0
      )

      // Add null checking for defaultServices.tickets
      const packageStore = usePackageStore()
      const defaultServices = packageStore.defaultServices
      const defaultServiceTicketsId = defaultServices?.tickets?.map(
        ticket => ticket.searchParams.ticketId
      ) || []

      const selectedTicketsId = state.tickets.map(ticket => ticket.ticketId)
      const howManyUpgradeFeeShouldBeApplied = selectedTicketsId.filter(
        ticketId => !defaultServiceTicketsId.includes(ticketId)
      ).length

      return (
        state.numberOfTickets *
        (ticketTotalPrice +
          howManyUpgradeFeeShouldBeApplied * (packageStore.eventUpgradeFee || 0))
      )
    },

    calculateCouponDiscount: state => (total) => {
      if (!state.coupon) {
        return 0
      }

      const packageStore = usePackageStore()
      const perPassengerFactor = state.coupon.isPerPassenger
        ? (packageStore.numberOfPassengers || 1)
        : 1
      let rawAmount = 0

      if (state.coupon.type === 'percent') {
        rawAmount = (state.coupon.amount * total) / 100
      }

      if (state.coupon.type === 'fixed') {
        rawAmount = state.coupon.amount
      }

      return rawAmount * perPassengerFactor
    },

    outwardFlightUpgradeFee: state => () => {
      if (state.isFlightLessBooking || !state.outwardFlight) {
        return 0
      }
      const packageStore = usePackageStore()
      const basePrice = packageStore.defaultOutwardFlightPrice || 0

      return Math.round(
        state.calculateServicesPrice().outwardFlightPrice - basePrice
      )
    },

    returnFlightUpgradeFee: state => () => {
      if (state.isFlightLessBooking || !state.returnFlight) {
        return 0
      }
      const basePrice = usePackageStore().package?.defaultReturnFlightPrice || 0

      return Math.round(
        state.calculateServicesPrice().returnFlightPrice - basePrice
      )
    },

    overNightStays: state => () => {
      const diff =
        new Date(state.endDate).getTime() - new Date(state.startDate).getTime()

      return diff / (1000 * 3600 * 24)
    },

    hotelUpgradeFee: state => (hotel, defaultHotelId) => {
      if (state.isHotelLessBooking) {
        return 0
      }

      const basePrice = state.baseHotels
        .find(defaultHotel => defaultHotel.defaultHotelId === defaultHotelId)
        .rooms.reduce((acc, room) => acc + room.price, 0)
      const hotelPrice = hotel.rooms.reduce((acc, room) => acc + room.price, 0)

      return Math.round(hotelPrice - basePrice)
    },

    hotelUpgradeFeePerNight: state => (hotel, defaultHotelId) => {
      return (
        state.hotelUpgradeFee(hotel, defaultHotelId) / state.overNightStays()
      )
    },

    ticketsUpgradeFees: state => () => {
      // Ticket calculation is based on 1 person
      const packageStore = usePackageStore()

      return state.tickets.map((ticket) => {
        const calculatedPrice = packageStore.calculatedTicketPrice?.(ticket.ticketId) || 0
        return {
          ticketId: ticket.ticketId,
          price: calculatedPrice * state.numberOfTickets
        }
      })
    },

    extraServicesUpgradeFee: state => () => {
      if (!state.tripExtendedDays) {
        return 0
      }

      const packageStore = usePackageStore()
      const dailyFee = packageStore.package?.extraServicesAdditionalDailyFee || 0

      return state.tripExtendedDays * dailyFee
    },

    totalUpgradeFee: state => () => {
      const hotelUpgradeFee = state.hotels.reduce(
        (total, hotel) =>
          total + state.hotelUpgradeFee(hotel, hotel.defaultHotelId),
        0
      )

      return (
        state.outwardFlightUpgradeFee +
        state.returnFlightUpgradeFee +
        hotelUpgradeFee +
        state
          .ticketsUpgradeFees()
          .reduce((total, ticket) => total + ticket.price, 0) +
        state.getExtraFees
      )
    },

    createHotelBook:
      () =>
        (
          hotel,
          searchParams,
          defaultHotelId,
          venue = null,
          orderNumber = null,
          serviceUniqueId = null
        ) => {
          return {
            ...hotel,
            searchParams,
            defaultHotelId,
            venue,
            orderNumber,
            serviceUniqueId
          }
        },

    getBookingServices: state => () => {
      const { outwardFlightPrice, returnFlightPrice, ticketsPrice } =
        state.calculateServicesPrice()
      // TODO: Cleanup flight
      let flight = null
      if (!state.isFlightLessBooking && state.outwardFlight && state.returnFlight) {
        flight = {
          // service unique id represents interal id for the service
          serviceUniqueId: state.outwardFlight?.uniqueId,
          outwardUid: state.outwardFlight?.uid,
          returnUid: state.returnFlight?.uid,
          outwardOrigin: state.outwardFlight?.originAirport,
          outwardDestination: state.outwardFlight?.destinationAirport,
          outwardUpgradeFee: state.outwardFlightUpgradeFee,
          outwardMaxChanges: (state.outwardFlight?.segments?.length || 1) - 1,
          outwardSegments: state.outwardFlight?.segments || [],
          outwardSupplierCode: state.outwardFlight?.supplierCode,
          outwardTotalPrice: outwardFlightPrice,
          returnOrigin: state.returnFlight?.originAirport,
          returnDestination: state.returnFlight?.destinationAirport,
          returnUpgradeFee: state.returnFlightUpgradeFee,
          returnMaxChanges: (state.returnFlight?.segments?.length || 1) - 1,
          returnSegments: state.returnFlight?.segments || [],
          returnSupplierCode: state.returnFlight?.supplierCode,
          returnTotalPrice: returnFlightPrice,
          departureDate: state.outwardFlight?.segments?.[0]?.departureDateAndTime,
          returnDate: state.returnFlight?.segments?.[0]?.departureDateAndTime,
          adults: state.adults,
          children: state.children,
          infants: 0,
          extraServicesUpgradeFee: state.extraServicesUpgradeFee
        }
      }

      let hotels = null
      let rooms = null

      if (!state.isHotelLessBooking) {
        hotels = state.hotels
          .map((hotel) => {
            rooms = state.rooms.map((room) => {
              // Inject room data (We need to assign room data which is in hotel.rooms to each room)
              // We iterate through hotel.rooms to find the room which has the same room type and wasn't assigned to any room yet
              // In addition we look for a room which type's caption contains the room's type to fulfill PremierInn hotels
              const roomInfo = hotel.rooms.filter((r) => {
                return (
                  (r.roomType === room.roomType ||
                    r.roomTypeCaption.toLowerCase().includes(room.roomType)) &&
                  !r.adults
                )
              })[0]

              return {
                ...room,
                ...roomInfo
              }
            })

            return {
              idOrSlug: hotel.slug,
              // service unique id represents internal id for the service
              serviceUniqueId: hotel.serviceUniqueId,
              checkin: hotel.searchParams.checkin,
              checkout: hotel.searchParams.checkout,
              upgradeFee: state.hotelUpgradeFee(hotel, hotel.defaultHotelId),
              totalPrice: hotel.rooms.reduce(
                (prev, cur) => prev + parseFloat(cur.price),
                0
              ),
              orderNumber: hotel.orderNumber,
              rooms
            }
          })
          .sort((a, b) => (a.orderNumber > b.orderNumber ? 1 : -1))
      }

      let transit = null

      if (!state.isTransitLessBooking) {
        transit = {
          outwardRouteId: state.transit.info.id,
          returnRouteId: state.transit.info.return.id,
          outwardTotalPrice: state.transit.outwardTotalPrice,
          returnTotalPrice: state.transit.returnTotalPrice,
          outwardTimepoint: state.transit.outwardTimepoint,
          returnTimepoint: state.transit.returnTimepoint
        }
      }

      const ticketsUpgradeFees = state.ticketsUpgradeFees()
      const ticketAverageTotalPrice = ticketsPrice / state.tickets.length
      const tickets = state.tickets.map((ticket) => {
        return {
          ticketId: ticket.ticketId,
          upgradeFee: ticketsUpgradeFees.find(
            upgradeTicket => upgradeTicket.ticketId === ticket.ticketId
          )?.price,
          totalPrice: ticketAverageTotalPrice
        }
      })

      return {
        services: {
          hotels,
          transit,
          flight,
          tickets
        }
      }
    },

    getBookingDiscount: () => () => {
      return {
        flight: parseFloat(
          useCmsStore().conditions.bookingConfig.flightLessBookingDiscount
        ),
        hotel: parseFloat(
          useCmsStore().conditions.bookingConfig.hotelLessBookingDiscount
        )
      }
    },

    hasSameHomeAirports: state => () => {
      const outwardOrigin = state.outwardFlight?.originAirport
      const returnDestination = state.returnFlight?.destinationAirport

      // Return false if either airport is missing
      if (!outwardOrigin || !returnDestination) {
        return false
      }

      return outwardOrigin === returnDestination
    }
  },

  actions: {
    resetBooking () {
      // Check if custom dates should be preserved
      const customDatesStore = useCustomDatesStore?.() // Optional for SSR
      const preserveCustomDates = customDatesStore?.hasCustomDates

      this.packageId = null
      this.hotels = []
      this.baseHotels = []

      // Only reset dates if no custom dates are active
      if (!preserveCustomDates) {
        this.startDate = null
        this.endDate = null
      }

      this.tickets = []
      this.isLimitedTicketNumber = false
      this.numberOfTickets =
        Booking.defaultNumberOfAdults + Booking.defaultNumberOfChildren
      this.outwardFlight = null
      this.returnFlight = null
      this.adults = Booking.defaultNumberOfAdults
      this.children = Booking.defaultNumberOfChildren
      this.rooms = Booking.defaultRoom
      this.contactInfo = null
      this.passengers = []
      this.numberOfHasTicketPassengers = 0
      this.changedPrices = []
      this.comment = ''
      this.hasInsurance = ''
      this.hasMatchInsurance = ''
      this.hasAcceptedDifferentHomeAirports = false
      this.hasAcceptedExtraordinaryConditions = false
      this.hasAcceptedPremiumUpgrade = false
      this.isResidualSaleBooking = false
      this.coupon = ''
      this.isChangedPricesAccepted = false
      this.isFlightLessBooking = false
      this.isHotelLessBooking = false
      this.tripExtendedDays = 0

      // Notify custom dates store about reset
      if (customDatesStore) {
        customDatesStore.protectFromBookingStoreReset()
      }
    },
    setRoomsAdultsChildren (rooms) {
      const adults = rooms.reduce((prev, current) => prev + current.adults, 0)
      const children = rooms.reduce(
        (prev, current) => prev + current.children.length,
        0
      )
      this.adults = adults
      this.children = children
      this.rooms = rooms

      // Sync passenger data with flight store
      this.syncPassengerDataWithFlightStore()
    },

    /**
     * Sync passenger data with flight store to ensure consistency
     */
    syncPassengerDataWithFlightStore() {
      try {
        const flightStore = useFlightStore()
        flightStore.syncPassengerData()
      } catch (error) {
        // Fail silently if flight store is not available (e.g., during SSR)
        console.warn('Could not sync passenger data with flight store:', error.message)
      }
    },
    async validateCoupon (couponCode) {
      const { coupon } = await couponService.detail(couponCode)
      this.coupon = coupon

      return coupon
    },
    async availabilityAndPrice () {
      // Update insurances on only ticket bookings
      if (this.isFlightLessBooking && this.isHotelLessBooking) {
        this.hasInsurance = false
        this.hasMatchInsurance = false
      }

      const packageStore = usePackageStore()
      const packageSlug = packageStore.package?.slug

      if (!packageSlug) {
        throw new Error('Package not loaded')
      }

      const {
        price: { hotels, outwardFlight, returnFlight, tickets, total }
      } = await packageService.serviceAvailabilityAndPrice(
        packageSlug,
        {
          ...this.$state,
          ...this.getBookingServices()
        }
      )

      const {
        outwardFlightPrice,
        returnFlightPrice,
        hotelsPrice,
        ticketsPrice
      } = this.calculateServicesPrice()
      const totalPrice = this.calculateTotalPrice()

      const prices = [
        { name: 'hotel', ourPrice: hotelsPrice, theirPrice: hotels },
        {
          name: 'outwardFlight',
          ourPrice: outwardFlightPrice,
          theirPrice: outwardFlight
        },
        {
          name: 'returnFlight',
          ourPrice: returnFlightPrice,
          theirPrice: returnFlight
        },
        { name: 'tickets', ourPrice: ticketsPrice, theirPrice: tickets },
        { name: 'total', ourPrice: totalPrice, theirPrice: total }
      ]

      const changedPrices = prices.filter(
        price => Math.round(price.ourPrice) !== Math.round(price.theirPrice)
      )
      this.changedPrices = changedPrices
    },

    async preorder () {
      const packageStore = usePackageStore()
      const packageSlug = packageStore.package?.slug

      if (!packageSlug) {
        throw new Error('Package not loaded')
      }

      const {
        booking: { invoice }
      } = await preorderService.book(packageSlug, { ...this.$state })

      return invoice
    },

    async book () {
      const {
        booking: { invoice }
      } = await bookingService.book({
        ...this.$state,
        ...this.getBookingServices()
      })

      return invoice
    }
  }
})
